/**
 * Decides what to do with a queued booking the backend refused as a conflict.
 *
 * @author Milton Filho
 */
import { merge, of, race, timer } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import moment from 'moment';
import toast from 'react-hot-toast';
import { CustomEpic, AnyPayloadAction } from 'types/types';
import { GlobalState } from 'reducers';
import {
  BookingConflictedPayload,
  clearFailedItem,
  discardOfflineBooking,
} from 'modules/offlineSync/actions';
import { BOOKING_CONFLICTED } from 'modules/offlineSync/actionTypes';
import { CONFLICT_REFETCH_TIMEOUT_MS } from 'modules/offlineSync/constants';
import { getSchedule } from 'modules/schedule/actions';
import {
  GET_SCHEDULE_FAILURE,
  GET_SCHEDULE_SUCCESS,
} from 'modules/schedule/actionTypes';

/**
 * Whether the schedule now holds a real appointment for the refused slot.
 *
 * "Real" means: not one of the optimistic cards this booking created. Compared
 *   as instants rather than strings, because the backend formats its own
 *   timestamps and an offset difference would otherwise read as a mismatch.
 */
const hasRealAppointmentForSlot = (
  state: GlobalState,
  { tempAppointmentIds, appDatetime, patientId }: BookingConflictedPayload,
): boolean => {
  const appointments = state?.appointments?.normalized;
  if (!appointments || !appDatetime) return false;

  const wanted = moment(appDatetime);
  if (!wanted.isValid()) return false;

  return Object.values(appointments).some(
    appointment =>
      !!appointment &&
      !tempAppointmentIds.includes(appointment.id) &&
      appointment.patientId === patientId &&
      moment(appointment.appDatetime).isSame(wanted),
  );
};

/**
 * A conflict has two causes and they want opposite treatment, so this refuses
 * to guess: it asks the schedule and reads the answer.
 *
 * The backend measured both behind the same old `500`:
 *
 *   - **the booking we made.** Clinicient created it and the write to Zoho
 *     failed, so we were told the create failed and the retry then collided
 *     with our own appointment. Our schedule reads their side, so it may not
 *     know the appointment exists.
 *   - **a slot already taken by something else**, created outside the API. The
 *     first attempt conflicts immediately.
 *
 * `hadFailedBefore` separates them: the first cause always has a failure behind
 *   it, because that failure is what produced the retry. The second conflicts on
 *   its first attempt.
 *
 * But that only decides where to *start*. The `409` proves the slot is taken in
 *   Clinicient; it proves nothing about our schedule. So even when the item
 *   looks like a duplicate, the optimistic card is only removed once a refetch
 *   has actually surfaced the real appointment. If the refetch comes back
 *   without it — or does not come back — the card stays.
 *
 * That asymmetry is the point. Leaving a card that should have gone shows a
 *   duplicate for a while. Removing one that should have stayed empties a slot
 *   that Clinicient holds, and the therapist books over a real appointment.
 */
export const resolveBookingConflict$: CustomEpic<BookingConflictedPayload> = (
  action$,
  state$,
) =>
  action$.pipe(
    ofType(BOOKING_CONFLICTED),
    switchMap(({ payload }) => {
      const { queueItemId, tempAppointmentIds, hadFailedBefore } = payload;

      // Retrying is over either way: the queue item goes. What differs is
      //   whether the card it created goes with it.
      const keepTheCard = (): AnyPayloadAction[] => {
        toast.error(
          'That time is already taken. We could not confirm the booking — ' +
            'check the schedule before booking it again.',
        );
        return [clearFailedItem({ queueItemId })];
      };

      const dropTheCard = (): AnyPayloadAction[] => {
        toast.success('That appointment was already booked.');
        return [discardOfflineBooking({ queueItemId, tempAppointmentIds })];
      };

      // Almost certainly the appointment we created ourselves, and our schedule
      //   is the last place to learn about it. Nothing to uncover by refetching,
      //   so the card stays and the retry stops.
      if (hadFailedBefore) return of(...keepTheCard());

      // Otherwise: ask, then decide.
      // Only a *successful* refetch counts as an answer. A failure or a
      //   timeout leaves the schedule as unverified as it was, and the state
      //   must not be consulted in that case: it may already hold a matching
      //   appointment from an earlier fetch, and reading it would let a
      //   question we never got to ask decide the outcome.
      const answered$ = race(
        action$.pipe(
          ofType(GET_SCHEDULE_SUCCESS),
          map(() => true),
        ),
        merge(
          action$.pipe(ofType(GET_SCHEDULE_FAILURE)),
          timer(CONFLICT_REFETCH_TIMEOUT_MS),
        ).pipe(map(() => false)),
      ).pipe(
        take(1),
        switchMap(answered =>
          of(
            ...(answered && hasRealAppointmentForSlot(state$.value, payload)
              ? dropTheCard()
              : keepTheCard()),
          ),
        ),
      );

      // The listener is merged first on purpose: `merge` subscribes left to
      //   right, and putting the dispatch first would emit it before anything
      //   was listening for the answer. Harmless today, since the response is
      //   an HTTP round trip away, but only by accident.
      return merge(answered$, of(getSchedule({})));
    }),
  );
